import React, { useContext, useMemo, useCallback, useEffect } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { Router } from '@techstyle/next-routes';
import { FormattedMessage } from '@techstyle/react-intl';

import {
  trackBuildASetButtonClick,
  trackCompleteByoSet,
} from '../../actions/byo';
import { toggleCartDrawer } from '../../actions/cart';
import { addItemToByoSet } from '../../actions/checkout';
import { desktop } from '../../styles';
import { BasicButton } from '../../styles/button';
import {
  isByoSaleItem as getIsByoSaleItem,
  getMembershipCartItem,
  isClearanceItem as getIsClearanceItem,
} from '../../utils/selectors';
import { useMembership } from '../../utils/useMembership';
import { Component as ByoProvider } from '../ByoProvider';
import QuickViewProvider from '../QuickViewProvider/QuickViewProvider';
import { Component as SpinningLoader } from '../SpinningLoader';
import { Component as StaticIcon } from '../StaticIcon';
import ToastContext from '../ToastContext/ToastContext';

export const EXIT_FLOW_TIMEOUT_MS = 1000;

const Wrapper = styled.div``;

const BuildASetButtonContainer = styled.div`
  padding: ${props => props.theme.spacing.small}px;
  margin: 0 -${props => props.theme.spacing.small}px;
  ${desktop`
   ${props => (!props.isSingleAddToBagCTA ? `margin: 0;` : ``)}
  `}
  background-color: ${props => props.theme.colors.lavender100};
  box-shadow: 0 2px 4px ${props => props.theme.colors.lavender250};
`;

const buildASetButtonStyle = css`
  height: 44px;
  min-width: 200px;
  margin-bottom: 12px;
  background-color: ${({ theme }) => theme.colors.active};
  border-color: ${({ theme }) => theme.colors.active};

  &:disabled {
    background-color: ${({ theme }) => theme.colors.active};
    border-color: ${({ theme }) => theme.colors.active};
    color: ${({ theme }) => theme.colors.white};
    opacity: 0.5;
  }
`;

const defaultButtonStyle = css`
  height: 44px;
  min-width: 200px;
  margin: ${props => props.theme.spacing.moderate}px 0;
`;

const BuildASetButton = styled(BasicButton).attrs({
  fullWidth: true,
  buttonStyle: buildASetButtonStyle,
})``;

const AddToSetButton = styled(BasicButton).attrs({
  fullWidth: true,
  buttonStyle: defaultButtonStyle,
})``;

const CompleteSetButton = styled(BasicButton).attrs({
  fullWidth: true,
  buttonStyle: defaultButtonStyle,
})``;

const PlusIcon = styled(StaticIcon).attrs({
  type: 'plus',
  size: 8,
})``;

const TextContainer = styled.div`
  font-size: 12px;
  font-weight: 300;
  line-height: 16px;
  text-align: center;
  letter-spacing: 0.3px;
  padding: 0 36px;
`;

export default function ByoCTAs({
  checkByoErrorState,
  masterProduct,
  productToAdd,
  selectedProductQuantity = 1,
}) {
  const dispatch = useDispatch();
  const { isVip } = useMembership();
  const { fetchedDate } = useSelector(state => state.checkout);
  const membershipItem = useSelector(getMembershipCartItem);
  const isQuantityOne = selectedProductQuantity === 1;
  const { showMessage: showToastMessage } = useContext(ToastContext.Context);
  const {
    showByoOptionsModal,
    isFlowInitiated,
    isInterstitialFlow,
    clickedFromByoGrids,
    isLastStep,
    isLoading,
    byoGroupCode,
    increaseByoStep,
    isProgressBarModalOpen,
    isByoCtaEnabled,
    selectedByoSet,
    setIsProgressBarModalOpen,
    setIsByoCtaEnabled,
    setIsLoading,
    setByoSetCompleted,
    trackAddedItems,
    exitByoFlow,
  } = useContext(ByoProvider.Context);
  const shouldDisableCta = !isByoCtaEnabled || !isQuantityOne;
  const isSingleAddToBagCTA = isVip || membershipItem || fetchedDate;
  const isSaleProduct = useSelector(state =>
    getIsByoSaleItem(state, { product: masterProduct })
  );
  const isClearanceItem = useSelector(state =>
    getIsClearanceItem(state, { product: masterProduct })
  );

  const { closeQuickViewModal } = useContext(QuickViewProvider.Context);

  // set cta back to true when unmount
  useEffect(() => {
    return () => setIsByoCtaEnabled(true);
  }, [setIsByoCtaEnabled]);

  const handleBuildASetClick = useCallback(() => {
    const errors = checkByoErrorState();
    if (!errors) {
      showByoOptionsModal();
      if (productToAdd && masterProduct) {
        dispatch(
          trackBuildASetButtonClick({
            productId: productToAdd.product_id,
            productCategoryId: masterProduct.default_product_category_id,
          })
        );
      }
    }
  }, [
    checkByoErrorState,
    dispatch,
    masterProduct,
    productToAdd,
    showByoOptionsModal,
  ]);

  const handleAddItemToByoSet = useCallback(async () => {
    const errors = checkByoErrorState();
    if (!errors && productToAdd) {
      setIsByoCtaEnabled(false);
      setIsLoading(true);
      try {
        await dispatch(
          addItemToByoSet(
            productToAdd.product_id,
            selectedByoSet.byoProductId,
            byoGroupCode,
            1
          )
        );
        trackAddedItems(masterProduct);
        increaseByoStep();
        if (!isInterstitialFlow) {
          setIsProgressBarModalOpen(true);
        }
        setTimeout(closeQuickViewModal, EXIT_FLOW_TIMEOUT_MS);
      } catch (err) {
        setIsByoCtaEnabled(true);
        showToastMessage(
          <FormattedMessage
            id="site_byo.general_error"
            defaultMessage="Oops we’ve encountered an error. Try again"
          />
        );
      } finally {
        setIsLoading(false);
      }
    }
  }, [
    byoGroupCode,
    checkByoErrorState,
    closeQuickViewModal,
    dispatch,
    increaseByoStep,
    masterProduct,
    productToAdd,
    selectedByoSet.byoProductId,
    setIsByoCtaEnabled,
    setIsLoading,
    setIsProgressBarModalOpen,
    showToastMessage,
    trackAddedItems,
  ]);

  const handleCompleteSet = useCallback(async () => {
    const errors = checkByoErrorState();
    if (!errors && productToAdd) {
      setIsByoCtaEnabled(false);
      setIsLoading(true);
      try {
        const { byoProductId } = selectedByoSet;
        const response = await dispatch(
          addItemToByoSet(
            productToAdd.product_id,
            byoProductId,
            byoGroupCode,
            1
          )
        );

        dispatch(trackCompleteByoSet(response.payload, byoProductId));
        trackAddedItems(masterProduct);
        setByoSetCompleted();
        setTimeout(() => {
          if (!isInterstitialFlow) {
            dispatch(toggleCartDrawer(true, { fetchCart: true }));
            exitByoFlow();
          } else {
            Router.push('/cart').then(exitByoFlow);
          }
        }, EXIT_FLOW_TIMEOUT_MS);
        showToastMessage(
          <FormattedMessage
            id="global_checkout.cart_item_added_success_message"
            defaultMessage="Item added"
          />
        );
      } catch (err) {
        setIsByoCtaEnabled(true);
        showToastMessage(
          <FormattedMessage
            id="site_byo.general_error"
            defaultMessage="Oops we’ve encountered an error. Try again"
          />
        );
      } finally {
        setIsLoading(false);
      }
    }
  }, [
    byoGroupCode,
    checkByoErrorState,
    dispatch,
    exitByoFlow,
    masterProduct,
    productToAdd,
    selectedByoSet,
    setByoSetCompleted,
    setIsByoCtaEnabled,
    setIsLoading,
    showToastMessage,
    trackAddedItems,
    isInterstitialFlow,
  ]);

  const byoCtas = useMemo(() => {
    if (isFlowInitiated && !!(clickedFromByoGrids || isInterstitialFlow)) {
      return isLastStep && (!isProgressBarModalOpen || isInterstitialFlow) ? (
        <CompleteSetButton
          onClick={handleCompleteSet}
          disabled={shouldDisableCta}
          data-autotag="complete_set_cta"
        >
          {isLoading ? (
            <SpinningLoader isLoading />
          ) : (
            <FormattedMessage
              id="site_pdp.complete_set_button"
              defaultMessage="Complete Set"
            />
          )}
        </CompleteSetButton>
      ) : (
        <AddToSetButton
          onClick={handleAddItemToByoSet}
          disabled={shouldDisableCta}
          data-autotag="add_to_set_cta"
        >
          {isLoading ? (
            <SpinningLoader isLoading />
          ) : (
            <FormattedMessage
              id="site_pdp.add_to_set_button"
              defaultMessage="Add To Set"
            />
          )}
        </AddToSetButton>
      );
    }

    const renderTextBelowButton = () => {
      if (isQuantityOne) {
        // We use isClearanceItem here as a fallback in case we don't have the sale fplId available
        // to reference because it's loaded dynamically from the /byo/sale route
        return isSaleProduct || isClearanceItem ? (
          <FormattedMessage
            id="site_pdp.limited_time_byo"
            defaultMessage="You've unlocked this Xclusive Perk!"
          />
        ) : (
          <FormattedMessage
            id="site_pdp.byo_unlock_message"
            defaultMessage="You’ve unlocked “Build a Set” with your Xtra VIP Credit!"
          />
        );
      } else {
        return (
          <FormattedMessage
            id="site_pdp.byo_error_message"
            defaultMessage={`"Build a Set" is only available with a quantity of 1`}
          />
        );
      }
    };

    return (
      <BuildASetButtonContainer isSingleAddToBagCTA={isSingleAddToBagCTA}>
        <BuildASetButton
          data-autotag="+build_a_set"
          disabled={shouldDisableCta}
          onClick={handleBuildASetClick}
        >
          {isLoading ? (
            <SpinningLoader isLoading />
          ) : (
            <>
              <PlusIcon />
              <FormattedMessage
                id="site_pdp.build_a_set_button"
                defaultMessage="Build a Set"
              />
            </>
          )}
        </BuildASetButton>
        <TextContainer data-autotag="+build_a_set_text">
          {renderTextBelowButton()}
        </TextContainer>
      </BuildASetButtonContainer>
    );
  }, [
    clickedFromByoGrids,
    handleAddItemToByoSet,
    handleBuildASetClick,
    handleCompleteSet,
    isFlowInitiated,
    isLastStep,
    isLoading,
    isProgressBarModalOpen,
    isSaleProduct,
    isQuantityOne,
    isSingleAddToBagCTA,
    shouldDisableCta,
    isInterstitialFlow,
  ]);

  return <Wrapper>{byoCtas}</Wrapper>;
}

ByoCTAs.propTypes = {
  checkByoErrorState: PropTypes.func,
  masterProduct: PropTypes.object,
  selectedProductQuantity: PropTypes.number,
  productToAdd: PropTypes.object,
};
